import React from "react";
var __jsx = React.createElement;
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTag } from "@fortawesome/pro-regular-svg-icons";
import { Button } from "@/components/button";
import { bulkUpdateTags } from "@/services/patient-tags";
import { useUpdateActionState } from "@/hooks/tasks";
import { usePatientTagsStore } from "@/stores/patient-tags";
import { useCustomFieldsQuery, usePatientTagsQuery } from "@/hooks/patient-tags";
import { useTagUndoStore } from "@/stores/tag-undo";
import { useEmrContext } from "@/hooks/emr-context";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";
import { formatTagValue, patchServerTags } from "@/utils/tags";
import { TagAction } from "../tag-action";
/** Renders the "Update tags" group with a key:value row per tag and an apply-all control. */
export function TagActionsGroup(_ref) {
  var tagActions = _ref.tagActions,
    patientUid = _ref.patientUid,
    reminderId = _ref.reminderId,
    isActionDone = _ref.isActionDone,
    _onDone = _ref.onDone,
    _onUndo = _ref.onUndo,
    onDelete = _ref.onDelete;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useEmrContext = useEmrContext(),
    workspaceId = _useEmrContext.workspaceId;
  var patientTags = usePatientTagsStore(function (s) {
    return s.serverTags;
  });
  var setServerTags = usePatientTagsStore(function (s) {
    return s.setServerTags;
  });
  var _useCustomFieldsQuery = useCustomFieldsQuery(workspaceId),
    customFields = _useCustomFieldsQuery.data;
  var _usePatientTagsQuery = usePatientTagsQuery(Number(patientUid)),
    fetchedPatientTags = _usePatientTagsQuery.data;
  var _useUpdateActionState = useUpdateActionState(patientUid, reminderId),
    updateActionState = _useUpdateActionState.mutate;
  var rememberOldValue = useTagUndoStore(function (s) {
    return s.remember;
  });
  useEffect(function () {
    if (fetchedPatientTags) {
      setServerTags(fetchedPatientTags.map(function (tag) {
        return {
          tag_id: tag.tag_id,
          response: tag.response
        };
      }));
    }
  }, [fetchedPatientTags, setServerTags]);
  var pendingActions = tagActions.filter(function (action) {
    return !isActionDone(action);
  });
  var currentValueFor = function currentValueFor(tagId) {
    var _patientTags$find$res, _patientTags$find;
    return (_patientTags$find$res = patientTags === null || patientTags === void 0 || (_patientTags$find = patientTags.find(function (tag) {
      return tag.tag_id === tagId;
    })) === null || _patientTags$find === void 0 ? void 0 : _patientTags$find.response) !== null && _patientTags$find$res !== void 0 ? _patientTags$find$res : null;
  };
  var _useMutation = useMutation(function () {
      return bulkUpdateTags(Number(patientUid), {
        updated_tags: pendingActions.map(function (action) {
          var payload = action.payload;
          return {
            tag_id: payload.id,
            response: payload.response
          };
        }),
        deleted_tags: []
      });
    }, {
      onSuccess: function onSuccess() {
        var updatedTags = pendingActions.map(function (action) {
          var payload = action.payload;
          return {
            tag_id: payload.id,
            response: payload.response
          };
        });
        var current = usePatientTagsStore.getState().serverTags;
        usePatientTagsStore.getState().setServerTags(patchServerTags(current, updatedTags, []));
        pendingActions.forEach(function (action) {
          updateActionState({
            actionId: action.id,
            state: "done"
          });
          _onDone(action.id);
        });
      }
    }),
    applyAll = _useMutation.mutate,
    isApplyingAll = _useMutation.isLoading;
  var tagKeyFor = function tagKeyFor(tagId) {
    var _customFields$find$ta, _customFields$find;
    return (_customFields$find$ta = customFields === null || customFields === void 0 || (_customFields$find = customFields.find(function (field) {
      return field.id === tagId;
    })) === null || _customFields$find === void 0 ? void 0 : _customFields$find.tag) !== null && _customFields$find$ta !== void 0 ? _customFields$find$ta : "";
  };
  var handleApplyAll = function handleApplyAll() {
    trackEvent(ANALYTICS_EVENTS.TAG_ACTION_APPLY_ALL);
    pendingActions.forEach(function (action) {
      var payload = action.payload;
      var current = currentValueFor(payload.id);
      if (formatTagValue(current).trim() !== "") rememberOldValue(action.id, current);
    });
    applyAll();
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-3"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-center justify-between gap-2"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-2"
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faTag,
    className: "w-3.5 h-3.5 text-sds-brand-default"
  }), /*#__PURE__*/React.createElement("span", {
    className: "text-style-label-caps text-sds-base-secondary"
  }, t("label.update_tags", {
    defaultValue: "Update tags"
  }))), pendingActions.length > 1 && /*#__PURE__*/React.createElement(Button, {
    "data-testid": "tag-actions-apply-all",
    intent: "brand",
    variant: "secondary",
    size: "sm",
    loading: isApplyingAll,
    onClick: handleApplyAll
  }, t("button.apply_all", {
    defaultValue: "Apply all"
  }))), tagActions.map(function (action) {
    var payload = action.payload;
    return /*#__PURE__*/React.createElement(TagAction, {
      key: action.id,
      action: action,
      tagKey: tagKeyFor(payload.id),
      currentValue: currentValueFor(payload.id),
      patientUid: patientUid,
      reminderId: reminderId,
      isDone: isActionDone(action),
      onDone: function onDone() {
        return _onDone(action.id);
      },
      onUndo: function onUndo() {
        return _onUndo(action.id);
      },
      onDelete: onDelete
    });
  }));
}